import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { AchievementWatcher } from '@/components/engagement';
import { FormatProvider } from '@/lib/money';
import { useHotkeys } from '@/lib/keyboard';
import { useSession } from '@/lib/session';
import { useAppStore } from '@/lib/store';
import { CommandPalette } from './CommandPalette';
import { ErrorBoundary } from './ErrorBoundary';
import { HelpPanel } from './HelpPanel';
import { MobileNav } from './MobileNav';
import { usePageMeta } from './nav';
import { ShortcutsDialog } from './ShortcutsDialog';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { TourProvider } from './TourProvider';
import { GuestBanner, InvitesBanner, UpgradeSheet, VerifyEmailBanner } from './Growth';
import { BRAND } from '@/brand';
import { FeedAutoSync } from '@/routes/accounts/BankFeeds';
import { cn } from '@/lib/cn';

/** Pages holding the transaction register use the full width of a big screen. */
const WIDE = /^\/(transactions|accounts\/[^/]+)\/?$/;

/** Global shortcuts that navigate or open panels (⌘K and `/` live in CommandPalette; Esc is handled by Radix overlays). */
const GlobalShortcuts = () => {
  const navigate = useNavigate();
  const openHelp = useAppStore((s) => s.openHelp);
  useHotkeys({
    '?': () => openHelp(null),
    // a register on the page (Transactions, account detail) opens its own editor for `n`
    n: () => (document.querySelector('[data-register]') ? false : navigate('/transactions?new=1')),
    'g h': () => navigate('/'),
    'g a': () => navigate('/accounts'),
    'g t': () => navigate('/transactions'),
    'g b': () => navigate('/budget'),
    'g g': () => navigate('/goals'),
    'g i': () => navigate('/invest'),
    'g r': () => navigate('/reports'),
  });
  return null;
};

/** Sets the document title, records recent pages and announces navigation to screen readers. */
const RouteEffects = () => {
  const handle = usePageMeta();
  const location = useLocation();
  const pushRecent = useAppStore((s) => s.pushRecent);
  const [announce, setAnnounce] = useState('');
  const first = useRef(true);
  const title = handle?.title ?? `${BRAND.name}`;

  // the page's own title arrives after its lazy chunk mounts its PageHeader
  useEffect(() => {
    document.title = title === `${BRAND.name}` ? `${BRAND.name}` : `${title} · ${BRAND.name}`;
    if (handle) pushRecent({ path: location.pathname + location.search, title });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, location.pathname]);

  // announce once the new page's title has settled (the old page can stay mounted while the new chunk loads)
  const pending = useRef(false);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    pending.current = true;
    document.getElementById('main')?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);
  useEffect(() => {
    if (!pending.current) return;
    const t = setTimeout(() => {
      pending.current = false;
      setAnnounce(`${title} page`);
    }, 400);
    return () => clearTimeout(t);
  }, [title, location.pathname]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic>
      {announce}
    </div>
  );
};

/**
 * Authenticated application shell: sidebar (desktop), top bar, bottom nav (mobile), and the global overlays
 * (command palette, help panel, shortcuts, tours, achievement toasts). Route content renders in <main>.
 */
export const AppLayout = () => {
  const { entity, user } = useSession();
  const location = useLocation();
  return (
    <FormatProvider currency={entity?.baseCurrency ?? user?.displayCurrency ?? 'AUD'} locale={entity?.locale ?? user?.locale ?? 'en-AU'}>
      <a
        href="#main"
        className="sr-only z-[100] rounded-md bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to content
      </a>
      <div className="flex min-h-dvh bg-background">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <GuestBanner />
          <VerifyEmailBanner />
          <InvitesBanner />
          <TopBar />
          <main id="main" tabIndex={-1} className={cn("mx-auto w-full flex-1", WIDE.test(location.pathname) ? "max-w-[120rem]" : "max-w-7xl", " px-4 pb-24 pt-6 outline-none sm:px-6 md:pb-10 lg:px-8")}>
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
      <MobileNav />
      <CommandPalette />
      <HelpPanel />
      <ShortcutsDialog />
      <TourProvider />
      <GlobalShortcuts />
      <RouteEffects />
      <AchievementWatcher />
      <FeedAutoSync entityId={entity?.id} />
      <UpgradeSheet />
    </FormatProvider>
  );
};
